import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, "")}${path}`
  }
  return path
}

export type DelegationStatus = "ALL" | "ACTIVE" | "UPCOMING" | "EXPIRED" | "INACTIVE"

export interface DelegationDto {
  delegationId: string
  tenantId: string
  delegatorUserId: string
  delegatorName?: string | null
  delegatorEmail?: string | null
  delegateeUserId: string
  delegateeName?: string | null
  delegateeEmail?: string | null
  effectiveFrom: string
  effectiveTo?: string | null
  reason?: string | null
  isActive: boolean
  status: DelegationStatus | string
  createdAt: string
  createdBy?: string | null
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface DelegationSummaryDto {
  total: number
  active: number
  upcoming: number
  expired: number
}

export interface DelegationSearchParams {
  tenantId: string
  currentUserId: string
  search?: string
  status?: DelegationStatus
  pageNumber?: number
  pageSize?: number
}

export interface DelegationSearchResultDto {
  items: DelegationDto[]
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
  summary: DelegationSummaryDto
  canManageAll: boolean
}

export interface DelegationUpsertRequest {
  tenantId: string
  currentUserId: string
  delegatorUserId: string
  delegateeUserId: string
  effectiveFrom: string
  effectiveTo?: string | null
  reason?: string | null
  isActive: boolean
}

export interface DelegationSetActiveRequest {
  tenantId: string
  currentUserId: string
  isActive: boolean
}

export interface DelegationDeleteRequest {
  tenantId: string
  currentUserId: string
}

export async function searchDelegations(params: DelegationSearchParams): Promise<DelegationSearchResultDto> {
  const response = await axios.get<DelegationSearchResultDto>(buildUrl("/api/v1/delegations"), {
    params,
  })
  return response.data
}

export async function getDelegation(
  delegationId: string,
  params: Pick<DelegationSearchParams, "tenantId" | "currentUserId">,
): Promise<DelegationDto> {
  const response = await axios.get<DelegationDto>(buildUrl(`/api/v1/delegations/${delegationId}`), {
    params,
  })
  return response.data
}

export async function createDelegation(payload: DelegationUpsertRequest): Promise<DelegationDto> {
  const response = await axios.post<DelegationDto>(buildUrl("/api/v1/delegations"), payload, {
    params: {
      tenantId: payload.tenantId,
      currentUserId: payload.currentUserId,
    },
  })
  return response.data
}

export async function updateDelegation(delegationId: string, payload: DelegationUpsertRequest): Promise<DelegationDto> {
  const response = await axios.post<DelegationDto>(buildUrl(`/api/v1/delegations/${delegationId}`), payload, {
    params: {
      tenantId: payload.tenantId,
      currentUserId: payload.currentUserId,
    },
  })
  return response.data
}

export async function setDelegationActive(
  delegationId: string,
  payload: DelegationSetActiveRequest,
): Promise<DelegationDto> {
  const response = await axios.patch<DelegationDto>(buildUrl(`/api/v1/delegations/${delegationId}/active`), payload, {
    params: {
      tenantId: payload.tenantId,
      currentUserId: payload.currentUserId,
    },
  })
  return response.data
}

export async function deleteDelegation(delegationId: string, payload: DelegationDeleteRequest): Promise<void> {
  await axios.delete(buildUrl(`/api/v1/delegations/${delegationId}`), {
    data: payload,
    params: {
      tenantId: payload.tenantId,
      currentUserId: payload.currentUserId,
    },
  })
}
